import { useStore, DEMO_MODE, IS_STAGING, IS_DEV_ENV } from '../store/StoreContext.jsx';
import { useLayout } from '../store/LayoutContext.jsx';
import { overallProgress } from '../store/completion.js';
import Icon from './Icon.jsx';

export default function Topbar({
  onPresentation,
  onOpenConsole,
}) {
  const { data, autosaveStatus, saveError, saveErrorKind, saveNow, reset, readOnly } = useStore();
  const { focusMode, toggleFocus, zoomMode, toggleZoom } = useLayout();

  const handleReset = () => {
    if (window.confirm('Reset all data and start over?')) reset();
  };

  // Average per-section progress across all PQ sections.
  const completion = overallProgress(data);

  const householdName = (() => {
    const f = data.family || {};
    const last = (f.client1LastName || '').trim();
    return last ? `${last} Household` : 'New Household';
  })();

  const autosavePill = (() => {
    // Nothing syncs in view mode, so a "Saved"/"Saving" pill would be a lie.
    if (readOnly) return <span className="pill">View only</span>;
    if (autosaveStatus === 'saving')
      return <span className="pill pill-saving">Saving…</span>;
    if (autosaveStatus === 'saved')
      return <span className="pill pill-saved">Saved</span>;
    if (autosaveStatus === 'blocked')
      return (
        <span
          className="pill pill-blocked"
          title="Entries are kept in this browser, but nothing saves to Salesforce until the client's first and last name are entered."
        >
          ⚠ Name required to save
        </span>
      );
    if (autosaveStatus === 'error') {
      if (saveErrorKind === 'required') {
        return (
          <span className="pill pill-error" title={saveError || 'A required field is missing.'}>
            ⚠ Name required
          </span>
        );
      }
      const isValidation = saveErrorKind === 'validation';
      return (
        <span
          className="pill pill-error"
          title={
            isValidation
              ? 'Some entries were not accepted. Please review your dates and required fields.'
              : 'We could not save. Please keep this tab open and contact IT.'
          }
        >
          {isValidation ? '⚠ Check entries' : '⚠ Save failed'}
        </span>
      );
    }
    return null;
  })();

  return (
    <header
      className={
        'topbar' +
        (IS_STAGING ? ' is-staging' : IS_DEV_ENV ? ' is-dev' : DEMO_MODE ? ' is-demo' : '') +
        (zoomMode ? ' is-zoom' : '')
      }
    >
      <div className="topbar-inner">
        <a className="brand" href="#" onClick={e => e.preventDefault()}>
          {/* The standard logo has an opaque brand-blue rectangle baked into
              the SVG, so it matches the production bar exactly and sits on any
              other colour as a blue sticker. Non-production bars get the same
              artwork with that rectangle removed, which lets the white lockup
              sit directly on whatever colour the environment uses. */}
          <img
            className="brand-logo"
            src={IS_STAGING || IS_DEV_ENV || DEMO_MODE ? '/pure-logo-onbrand.svg' : '/pure-logo.svg'}
            alt="Pure Financial Advisors"
          />
          <span className="brand-sep" />
          <span className="brand-context">
            <span className="brand-context-kicker">Onboarding</span>
            <span className="brand-context-name">{householdName}</span>
          </span>
        </a>
        <div className="top-actions">
          {DEMO_MODE && <span className="pill pill-demo" title="Standalone demo — not connected to Salesforce. Nothing entered here is saved to a client record.">Demo · Not for live client use</span>}
          {IS_STAGING && <span className="pill pill-staging" title="Staging environment — connected to the Salesforce sandbox, not production. Nothing entered here reaches a real client record.">Staging · Sandbox data</span>}
          {IS_DEV_ENV && <span className="pill pill-staging" title="Dev environment, connected to the Salesforce sandbox, not production. Nothing entered here reaches a real client record.">Dev · Sandbox data</span>}
          <div className="top-completion" title={`${completion}% complete`}>
            <span className="top-completion-label">{completion}% complete</span>
            <div className="top-completion-bar">
              <div
                className="top-completion-fill"
                style={{ width: `${completion}%` }}
              />
            </div>
          </div>
          <div className="autosave-live" role="status" aria-live="polite">
            {autosavePill}
          </div>
          {/* Zoom mode: the PQ in a half-width window beside a Zoom or Teams
              call. Sits before Focus because it supersedes it there: Focus
              widens the form by collapsing the panels, Zoom mode removes them.
              The name is the advisors' own for the situation. */}
          <button
            type="button"
            className={'btn btn-ghost-dark btn-zoom' + (zoomMode ? ' is-on' : '')}
            onClick={toggleZoom}
            title={
              zoomMode
                ? 'Exit Zoom mode: bring back the side panels'
                : 'Zoom mode: a compact layout for a half-screen window, with notes docked below the form'
            }
            aria-pressed={zoomMode}
          >
            <Icon name="columns" size={13} /> Zoom
          </button>
          <button
            type="button"
            className={'btn btn-ghost-dark btn-focus' + (focusMode ? ' is-on' : '')}
            onClick={toggleFocus}
            title={
              focusMode
                ? 'Show the insights panel'
                : 'Focus mode: hide the insights panel for a wider form'
            }
            aria-pressed={focusMode}
          >
            <Icon name="maximize" size={13} /> Focus
          </button>
          {onPresentation && (
            <button
              type="button"
              className="btn btn-ghost-dark btn-present"
              onClick={onPresentation}
            >
              <Icon name="play" size={13} /> Present
            </button>
          )}
          {!readOnly && (
            <button type="button" className="btn btn-primary" onClick={saveNow}>
              <Icon name="save" size={13} /> Save
            </button>
          )}
          {!readOnly && (
            <button
              type="button"
              className="btn btn-danger-ghost btn-reset"
              onClick={handleReset}
            >
              Reset
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
